// ─────────────────────────────────────────────────────────────
//  Research Report – Schema, Template, Validierung & Felddoku
//
//  Eine Research-Seite (Tabs „Research" + „Impact") wird vollständig
//  aus EINEM Report-Objekt gerendert. Dieses Modul ist die einzige
//  Quelle der Wahrheit für die Form dieses Objekts:
//
//   • createEmptyReport()  → leeres, vollständig strukturiertes Template
//   • normalizeReport(obj) → füllt fehlende Felder mit Defaults (robust
//                            gegen unvollständige / KI-generierte JSONs)
//   • validateReport(obj)  → prüft Pflichtfelder, Typen & Längen
//   • FIELD_DOCS           → strukturierte Doku (Pfad, Typ, Format, Länge,
//                            Stil, Beispiel) – im Admin sichtbar und für
//                            eine spätere API/KI-Generierung gedacht.
//   • slugify(name)        → URL-Slug aus dem Firmennamen.
//
//  WICHTIG: Alle Geld-Zahlen (Preise, Szenarien, ROI) werden NICHT im
//  Report gespeichert, sondern zur Laufzeit aus `config` über die
//  pricingEngine berechnet. Im Report stehen also nur die Stellschrauben
//  (Deal-Wert, Marge, Paket, Quoten ...) sowie alle TEXTE und SCORES.
// ─────────────────────────────────────────────────────────────

// Default-Config (Stellschrauben der Live-Ökonomie)
export const DEFAULT_CONFIG = {
  revenuePerClose: 15000,
  profitMargin: 0.35,
  tier: 1,
  valueBasedPricingEnabled: true,
  quality: { uniquenessScore: 7, marketNeedScore: 7, blueOceanScore: 7 },
  expectedApptsOverride: null,
  setup: 0,
  closeRate: 0.3,
  apptValueMode: 'auto',
  apptValueManual: 250,
  monthlyPriceMode: 'auto',
  monthlyPriceManual: 2860,
};

// URL-Slug aus dem Firmennamen (a–z, 0–9, Bindestriche)
export const slugify = (str = '') =>
  str
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

// Kleine Zufalls-ID (4-stellig) als Slug-Suffix – macht die URL schwerer zu
// erraten (z. B. „mustermann-gmbh-4821" statt nur „mustermann-gmbh").
export const genSlugSuffix = () => String(Math.floor(1000 + Math.random() * 9000));

// Slug aus Firmenname + Zufalls-ID. Wenn kein Name vorhanden ist, bleibt der
// Slug leer (damit nicht nur die nackte ID als URL entsteht).
export const slugWithId = (str = '', id) => {
  const base = slugify(str);
  if (!base) return '';
  return `${base}-${id || genSlugSuffix()}`;
};

// Zugangs-Passwort für eine Research-Seite.
// Format wie ein Produkt-/Kreditkarten-Schlüssel: 4 Gruppen à 4 Zeichen,
// z. B. "K7MR-P2QW-9TZX-A4VH". Erzeugt mit einem kryptografisch sicheren
// Zufallsgenerator aus einem mehrdeutigkeitsfreien Alphabet (kein 0/O, 1/I/l).
// Entropie ≈ 57^16 (~10^28) – praktisch nicht zu erraten, aber gut lesbar.
// Der `name`-Parameter wird bewußt ignoriert (Passwort hängt nicht mehr vom
// Firmennamen ab) und nur aus Kompatibilität mit bestehenden Aufrufen belassen.
// Muss konzeptionell mit der Backend-Logik (functions/index.js) übereinstimmen.
const PW_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
const PW_GROUPS = 4;
const PW_GROUP_LEN = 4;

export const genResearchPassword = () => {
  const total = PW_GROUPS * PW_GROUP_LEN;
  const alphabetLen = PW_ALPHABET.length;
  // Größter Vielfacher von alphabetLen unter 256 → Rejection-Sampling gegen Modulo-Bias.
  const limit = 256 - (256 % alphabetLen);
  const cryptoObj = (typeof window !== 'undefined' && window.crypto) || null;
  const randByte = () => {
    if (cryptoObj && cryptoObj.getRandomValues) {
      const b = new Uint8Array(1);
      cryptoObj.getRandomValues(b);
      return b[0];
    }
    return Math.floor(Math.random() * 256);
  };
  const chars = [];
  while (chars.length < total) {
    const b = randByte();
    if (b < limit) chars.push(PW_ALPHABET[b % alphabetLen]);
  }
  const groups = [];
  for (let i = 0; i < total; i += PW_GROUP_LEN) {
    groups.push(chars.slice(i, i + PW_GROUP_LEN).join(''));
  }
  return groups.join('-');
};

// Standard-Inhalt der Setup-Details-Ansicht.
// Dient als Vorlage: Wird mit jedem neuen Report mitgespeichert und kann auf
// der Live-Seite frei bearbeitet werden. Bestehende Reports ohne eigenes
// `kickoff`-Feld erhalten diesen Default automatisch über normalizeReport.
//
// Struktur:
//  • checklist – interne Setup-To-Dos (Account-Setup etc.), nur ein Häkchen.
//  • details   – Infos, die wir vom Kunden brauchen; jede Zeile hat zusätzlich
//                ein Wert-Feld zum Eintragen/Speichern der Antwort.
//  • notes     – freie Anmerkungen aus Gesprächen (Bulk-Import oder manuell).
export const DEFAULT_KICKOFF = {
  // Interne Setup-Checkliste – einfach abhaken, was erledigt ist.
  checklist: [
    { label: 'Domain wird gekauft', done: false },
    { label: 'Postfächer werden auf SalesGod eingerichtet', done: false },
    { label: 'Blacklist wurde hinzugefügt', done: false },
    { label: 'Notes & Infos werden abgelegt', done: false },
    { label: 'Kontaktliste wird eingefügt', done: false },
    { label: 'E-Mail-Signatur wird angelegt', done: false },
    { label: 'E-Mail Warmup wird aktiviert', done: false },
    { label: 'Team-Mitarbeiter wird hinzugefügt', done: false },
    { label: 'Kunden-Account wird hinzugefügt (falls gewünscht)', done: false },
  ],
  // Benötigte Infos vom Kunden – ein editierbarer Info-Hub.
  // { label (Titel), prompt (kurzer Hinweistext), type ('text' | 'pills'),
  //   value (Antwort, bei type='text'), tags (Werte-Pills, bei type='pills'),
  //   attachments (hochgeladene Dateien [{ name, url, type, size }] – z. B.
  //   Referenz-Mails als PDF oder eine Signatur als Bild/HTML; kombinierbar mit
  //   dem Textfeld) }
  details: [
    {
      label: 'Absender-Namen',
      prompt: 'Von welchen Personas / unter welchem Namen sollen die Mails gesendet werden?',
      type: 'text',
      value: '',
      tags: [],
      attachments: [],
    },
    {
      label: 'Domain-Auswahl',
      prompt: 'Welche Mail-Domain(s) sollen wir registrieren? (parallel zur Hauptdomain, z. B. firma.online)',
      type: 'pills',
      value: '',
      tags: [],
      attachments: [],
    },
    {
      label: "No-Go's beim Outreach",
      prompt: 'Was wollt ihr auf keinen Fall? Woran erkennen wir unpassende Kunden?',
      type: 'text',
      value: '',
      tags: [],
      attachments: [],
    },
    {
      label: 'E-Mail-Signatur',
      prompt: 'Vollständige E-Mail-Signatur der sendenden Person – als Text einfügen oder direkt hochladen (HTML-Datei oder Screenshot/Bild der Signatur).',
      type: 'text',
      value: '',
      tags: [],
      attachments: [],
    },
    {
      label: 'Referenztexte',
      prompt: 'Beispiel-Mails, die euch sprachlich gefallen / nicht gefallen – einfach unten als Datei (PDF, Dokument oder Screenshot) hochladen. Eure Ansprache erfassen wir über die E-Mail-DNA.',
      type: 'text',
      value: '',
      tags: [],
      attachments: [],
    },
    {
      label: 'Blacklist',
      prompt: 'Welche Unternehmen sollen wir NICHT kontaktieren (bestehende Kunden, gesperrte Kontakte aus CRM/Newsletter etc.)? Am besten als Excel-/CSV-Datei hochladen – wir können einzelne Adressen und ganze Domains sperren. Bitte über einen sicheren, verschlüsselten Weg übermitteln (keine unverschlüsselten Tabellen mit personenbezogenen Daten per Mail).',
      type: 'text',
      value: '',
      tags: [],
      attachments: [],
    },
    {
      label: 'Schlummer-Kunden-Liste',
      prompt: 'Liste eurer Schlummer-Kunden mit Name, E-Mail und Infos zu bisherigen Berührungspunkten/Käufen/Anfragen. Bevorzugt als Excel-/CSV-Datei (ebenfalls über einen sicheren, verschlüsselten Übertragungsweg).',
      type: 'text',
      value: '',
      tags: [],
      attachments: [],
    },
  ],
  // Weitere wichtige Anmerkungen aus Gesprächen [{ title, body }] (per Bulk-Import
  // oder manuell ergänzt). Stehen unter Checkliste & Details.
  notes: [],
};

// Vollständiges, leeres Template – als Startpunkt im Admin-Editor.
export const createEmptyReport = () => ({
  product: 'SalesGod',
  reportType: 'Opportunity Analysis',
  confidential: true,

  // Zusätzlich angehängte Ansichten (z. B. 'setup', 'email'), die rechts neben
  // Research/Impact erscheinen. Wird auf der Live-Seite über das „+" gepflegt.
  addedViews: [],

  // E-Mail Vault: editierbare Outreach-Mails [{ id, subject, body, reviews,
  // createdAt, source?, tag? }] der Email-Texte-Ansicht. `source: 'experiment'`
  // + `tag` (z. B. „Experimentierphase · Tag 3") markieren Texte, die der
  // Outreach-Manager im Experimentierphasen-Reporting hochgeladen hat.
  emails: [],

  // Setup-Details-Ansicht: editierbare Setup-Checkliste, benötigte Infos (mit
  // speicherbaren Werten) und freie Anmerkungen. Default siehe DEFAULT_KICKOFF.
  kickoff: JSON.parse(JSON.stringify(DEFAULT_KICKOFF)),

  // Kundenmagnet ("Magnetic Treasury"): individuell für diesen Kunden abgelegte
  // Lead-Magneten (kundensichtbarer Deliverable). Der Konkurrenz-Audit liegt NICHT
  // hier (admin-only, eigener DB-Pfad). Das Freischalten passiert über das Team
  // Dashboard (Flag am Kundenobjekt, nicht im Report).
  // magnets[]: { id, title, hook, description, type ('dfy'|'ideas'),
  //              tags[], effort, value, preview { url, name, type },
  //              attachments[{ name, url, type, size }], createdAt }
  leadMagnet: {
    magnets: [],
  },

  // Analyse-Ansicht (Research-Preview / Locked-Modus). Überblicks-Screen mit:
  //  - video: veed.io Share-/Embed-Link (oder eingefügter iframe-Code), optional.
  //  - companies[]: beispielhafte Zielfirmen-Liste { id, name, website, email, phone, description },
  //                 vom Team im Frontend gepflegt (manuell oder per CSV-Import).
  //  - emails[]: zwei toggelbare Mail-Konzepte { subject, body } (KI-vorgeneriert,
  //              editierbar). Keine Bewertung/Kommentare.
  analyse: {
    video: '',
    companies: [],
    emails: [
      { subject: '', body: '', toName: '', toCompany: '', kind: 'intro' },
      { subject: '', body: '', toName: '', toCompany: '', kind: 'followup' },
    ],
  },

  // Reporting-Ansicht (Experimentalphase): kurzer 30-Tage-Outreach-Report des
  // Outreach-Managers. Manuell ausfüllbar über die „+"-Ansicht „Reporting",
  // erreichbar über den öffentlichen Report-Link (Keyword-Passwort). Intern UND
  // für den Kunden sichtbar. Freitext-Antworten + optionale Kennzahlen.
  reporting: {
    submittedBy: '',
    submittedAt: null,
    summary: '',
    wins: '',
    challenges: '',
    learnings: '',
    recommendations: '',
    metrics: { contacted: '', replies: '', meetings: '' },
  },

  company: {
    name: '',
    industry: '',
    audience: '',
    website: '',
  },

  config: { ...DEFAULT_CONFIG, quality: { ...DEFAULT_CONFIG.quality } },

  verdict: {
    summary: ['', ''],
    leverage: {
      title: 'Wo der größte Hebel liegt',
      negative: '',
      positive: '',
    },
  },

  researchScores: [
    { label: 'Cold Outreach Potential', score: 7, max: 10, note: '' },
    { label: 'Markterreichbarkeit', score: 7, max: 10, note: '' },
    { label: 'Abschlussfähigkeit / Sales Cycle', score: 6, max: 10, note: '' },
  ],

  scenarios: { note: '' },

  riskFraming: {
    worstCaseMonths: 3,
    worstCaseTitle: '3 Monate Fee, kein einziger Abschluss',
    worstCaseSub: 'Reine Fixfee im Testzeitraum – ohne jeden gewonnenen Neukunden.',
    worstCaseStill: 'Und selbst in diesem Fall bekommst du:',
    worstCaseInsights: ['', '', '', ''],
    bestCaseSub: 'Profit-Upside im starken Szenario.',
    bestCaseStill: 'Zusätzlich entsteht:',
    bestCaseExtras: ['', '', ''],
    note: '',
  },

  feeVsUpside: {
    valueNote: '',
  },

  executiveSummary: {
    paragraphs: ['', ''],
    bestSegmentsTitle: 'Die größten Chancen liegen in:',
    bestSegments: [],
    closing: '',
  },

  research: {
    coldOutreach: {
      score: 17,
      max: 20,
      headline: '',
    },
    targetResearch: {
      ideas: [],
      keywords: [],
      hashtags: [],
      channels: ['E-Mail (Cold Outreach)'],
      decisionMakers: [],
      regions: [],
      revenueBands: [],
    },
    channelStrategy: {
      intro: '',
      channels: [],
    },
    angles: [
      { title: '', body: '' },
      { title: '', body: '' },
      { title: '', body: '' },
      { title: '', body: '' },
    ],
    // „Angebot des Kunden verstehen" – bei der Generierung aus dem Info-Dump
    // extrahiert (parallel zum Haupt-Call). Fehlende Punkte bleiben leer,
    // es wird nichts erfunden. Struktur:
    //  whatIsSold (Text), usp { text, points[] },
    //  salesSetup { have[], missing[] }, noBrainerOffer (Text, ehrlich/leer),
    //  noBrainerOffers (Liste editierbarer Angebote/Ideen: { title, text, idea }).
    offerAnalysis: {
      whatIsSold: '',
      usp: { text: '', points: [] },
      salesSetup: { have: [], missing: [] },
      noBrainerOffer: '',
      noBrainerOffers: [],
    },
    // „Zielgruppen-Tiefenrecherche" – eigener Opus-Doppel-Call (separat vom
    // Haupt-Report). Liefert einem Datenspezialisten alle Infos, um die perfekten
    // Firmen und Entscheider für Cold Outreach zu finden. Struktur:
    //  summary (Text), outreachNotes (Text für den Datenspezialisten),
    //  segments[] (Direktkunden + Partner/Multiplikatoren, je mit Tiefe inkl.
    //  konkreten Fundorten), dataSources[] (real benannte Plattformen/Verzeichnisse
    //  mit Beispielen). Wird NICHT über FIELD_DOCS validiert (eigene Sanitization
    //  im Backend), nur strukturell durch normalizeReport abgesichert.
    //
    // segments[]:   { name, type: 'direct'|'partner', description, whyFit,
    //                 companySize, revenueBand, decisionMakers[], buyingTriggers[],
    //                 searchHints[], sources[]{ name, url, note }, potential }
    // dataSources[]:{ name, type, url, note, examples[] }
    audienceResearch: {
      summary: '',
      segments: [],
      dataSources: [],
      // Briefing-Hinweise als Liste von { title, text }-Objekten (wenige, dafuer
      // hochwertige Hinweise). Alt-Strings/Fliesstext werden im Frontend robust
      // zu dieser Struktur normalisiert.
      outreachNotes: [],
    },
    // „Kunden-Psychologie" – eigener Opus-Call (Websuche-gegroundet), der die
    // Hormozi-Market-Research beantwortet: die tiefen Pains, Ängste, Frustrationen
    // und Fehler der idealen ZIELKUNDEN dieses Unternehmens. Speist die Magnet-/
    // Offer-Engine, damit Einstiegsangebote echte Nerven treffen. Wird (wie
    // audienceResearch) NICHT über FIELD_DOCS validiert, nur strukturell abgesichert.
    customerPsychology: {
      persona: '',
      summary: '',
      biggestResult: '',
      biggestProblem: '',
      topFrustration: '',
      successSteps: [],
      keepsAwake: '',
      humiliation: '',
      costOfInaction: '',
      urgentCrisis: '',
      dailyFrustrations: [],
      highLevelFix: '',
      biggestMistake: '',
      complaints: '',
    },
  },
});

// Deep-Merge: Default-Struktur, überschrieben mit den Werten aus `input`.
// Sorgt dafür, dass auch unvollständige Reports (z. B. von der KI) sicher
// rendern, ohne dass die Seite an fehlenden Feldern abstürzt.
const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const deepMerge = (base, override) => {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base) || Array.isArray(override)) {
    return override !== undefined ? override : base;
  }
  if (isPlainObject(base) && isPlainObject(override)) {
    const out = { ...base };
    Object.keys(override).forEach((k) => {
      out[k] = deepMerge(base[k], override[k]);
    });
    return out;
  }
  return override !== undefined ? override : base;
};

export const normalizeReport = (input = {}) => {
  const merged = deepMerge(createEmptyReport(), input);
  // config.quality separat absichern (deepMerge deckt es ab, hier nur Klarheit)
  merged.config = { ...DEFAULT_CONFIG, ...merged.config, quality: { ...DEFAULT_CONFIG.quality, ...(merged.config?.quality || {}) } };
  return merged;
};

// (Validierung wird data-driven aus FIELD_DOCS abgeleitet – siehe unten.)

// ── Strukturierte Felddokumentation ──
// Für die Admin-Referenz UND als Basis einer späteren API/KI-Generierung.
// type: 'string' | 'longtext' | 'string[]' | 'number' | 'percent' | 'enum' | 'bool'
export const FIELD_DOCS = [
  {
    group: 'Meta',
    fields: [
      { path: 'product', type: 'string', required: true, max: 40, style: 'Produktname, i. d. R. „SalesGod".', example: 'SalesGod' },
      { path: 'reportType', type: 'string', required: true, max: 40, style: 'Report-Typ.', example: 'Opportunity Analysis' },
      { path: 'confidential', type: 'bool', required: false, style: 'true → „Vertraulich"-Badge in der Nav.', example: 'true' },
    ],
  },
  {
    group: 'Unternehmen (company)',
    fields: [
      { path: 'company.name', type: 'string', required: true, max: 80, style: 'Voller Firmenname inkl. Rechtsform. Erscheint als großer Header-Titel.', example: 'SecuraNova Sicherheitstechnik GmbH' },
      { path: 'company.industry', type: 'string', required: true, max: 140, style: 'Branche(n), kommagetrennt, KEIN Satz, kein Punkt am Ende. Nur im Research-Tab sichtbar.', example: 'Sicherheitstechnik, Alarmanlagen, Zutrittskontrolle & Videoüberwachung' },
      { path: 'company.audience', type: 'string', required: true, max: 220, style: 'Zielgruppen als kommagetrennte Aufzählung, KEIN Satz, kein Punkt am Ende. Nur im Research-Tab.', example: 'Gewerbebetriebe, Hausverwaltungen, Lagerflächen, Arztpraxen, Kanzleien, Hotels' },
      { path: 'company.website', type: 'string', required: false, max: 120, style: 'Website-URL des Unternehmens inkl. https:// (falls bekannt), sonst leer. Wird unter dem Firmennamen angezeigt.', example: 'https://example.com' },
    ],
  },
  {
    group: 'Stellschrauben (config) – treiben ALLE Geld-Zahlen',
    fields: [
      { path: 'config.revenuePerClose', type: 'number', required: true, numMin: 100, numMax: 100000, range: '100–100000', style: 'Ø Kundenumsatz pro Abschluss in EUR (ganze Zahl).', example: '15000' },
      { path: 'config.profitMargin', type: 'percent', required: true, numMin: 0.1, numMax: 1, range: '0.1–1.0', style: 'Marge als Dezimalbruch (0.35 = 35 %).', example: '0.35' },
      { path: 'config.tier', type: 'enum', required: true, enumVals: [0, 1, 2], range: '0|1|2', style: '0 = Foundation, 1 = Plus, 2 = Premium (empfohlenes Paket).', example: '1' },
      { path: 'config.quality.uniquenessScore', type: 'number', required: false, numMin: 0, numMax: 10, range: '0–10', style: 'Qualitäts-Score Einzigartigkeit. Beeinflusst Preis-Nudge (±3 %).', example: '8' },
      { path: 'config.quality.marketNeedScore', type: 'number', required: false, numMin: 0, numMax: 10, range: '0–10', style: 'Qualitäts-Score Marktbedarf.', example: '8' },
      { path: 'config.quality.blueOceanScore', type: 'number', required: false, numMin: 0, numMax: 10, range: '0–10', style: 'Qualitäts-Score Wettbewerbsfreiheit.', example: '8' },
      { path: 'config.closeRate', type: 'percent', required: false, numMin: 0.01, numMax: 1.0, range: '0.01–1.0', style: 'Abschlüsse pro Termin (treibt nur ROI/Neukunden-Szenarien, NICHT den Termin-Preis). Default 0.3.', example: '0.3' },
      { path: 'config.expectedApptsOverride', type: 'number', required: false, nullable: true, numMin: 0, numMax: 100, range: 'null oder 0–100', style: 'Termine/Monat überschreiben; null = Tier-Default (6/12/24).', example: 'null' },
      { path: 'config.setup', type: 'number', required: false, numMin: 0, numMax: 100000, range: '0–100000', style: 'Setup-Gebühr in EUR (i. d. R. 0).', example: '0' },
      { path: 'config.apptValueMode', type: 'enum', required: false, enumVals: ['auto', 'manual'], range: "'auto'|'manual'", style: 'Termin-Preis automatisch berechnen oder manuell.', example: 'auto' },
      { path: 'config.apptValueManual', type: 'number', required: false, numMin: 0, numMax: 5000, range: '0–5000', style: 'Manueller Termin-Preis (nur bei apptValueMode=manual).', example: '250' },
      { path: 'config.monthlyPriceMode', type: 'enum', required: false, enumVals: ['auto', 'manual'], range: "'auto'|'manual'", style: 'Monatlichen Retainer automatisch berechnen oder manuell.', example: 'auto' },
      { path: 'config.monthlyPriceManual', type: 'number', required: false, numMin: 0, numMax: 50000, range: '0–50000', style: 'Manueller monatlicher Retainer (nur bei monthlyPriceMode=manual).', example: '2860' },
    ],
  },
  {
    group: 'Gesamt-Verdict (verdict) – Outcome unten im Impact-Tab',
    fields: [
      { path: 'verdict.summary', type: 'string[]', required: true, minItems: 1, maxItems: 3, count: '1–3 Absätze', max: 300, style: 'Vollständige Sätze. Gesamteinschätzung, ehrlich, keine Übertreibung. KEINE Geldbeträge/Prozentzahlen.', example: '["… starker Outbound-Case, weil …", "Der Markt ist gut erreichbar …"]' },
      { path: 'verdict.leverage.title', type: 'string', required: false, max: 60, style: 'Überschrift des Hebel-Callouts (kein Satz).', example: 'Wo der größte Hebel liegt' },
      { path: 'verdict.leverage.negative', type: 'string', required: true, max: 200, style: 'GENAU ein Satz: worin der Hebel NICHT liegt.', example: 'Der größte Hebel liegt nicht darin, möglichst viele Termine zu erzeugen.' },
      { path: 'verdict.leverage.positive', type: 'string', required: true, max: 220, style: 'GENAU ein Satz: worin der Hebel liegt.', example: 'Der größte Hebel liegt darin, gezielt jene Unternehmen zu erreichen, …' },
    ],
  },
  {
    group: 'Research-Scores (researchScores) – KI-Fit, fließt in den Gesamtscore',
    fields: [
      { path: 'researchScores[].label', type: 'string', required: true, max: 50, style: 'Name der Dimension (kein Satz).', example: 'Cold Outreach Potential' },
      { path: 'researchScores[].score', type: 'number', required: true, numMin: 0, range: '0–max', style: 'Erreichte Punkte (≤ max).', example: '8' },
      { path: 'researchScores[].max', type: 'number', required: true, numMin: 5, numMax: 20, range: '5–20', style: 'Maximalpunkte der Dimension. Summe der max ergibt den KI-Anteil (empf. ~30).', example: '10' },
      { path: 'researchScores[].note', type: 'string', required: false, max: 120, style: 'Kurze Begründung, ein Halbsatz, kein vollständiger Satz nötig.', example: 'Zielgruppe öffentlich auffindbar, Entscheider klar identifizierbar.' },
    ],
  },
  {
    group: 'Szenarien & Risiko-Framing',
    fields: [
      { path: 'scenarios.note', type: 'longtext', required: false, max: 320, style: '1–2 vollständige Sätze unter den Szenario-Karten. KEINE konkreten Zahlen.', example: 'Der Base Case setzt nicht voraus, dass jeder Termin sofort abschließt …' },
      { path: 'riskFraming.worstCaseMonths', type: 'number', required: false, numMin: 1, numMax: 12, range: '1–12', style: 'Worst Case = X Monate Fixfee ohne Abschluss.', example: '3' },
      { path: 'riskFraming.worstCaseTitle', type: 'string', required: false, max: 60, style: 'Headline der Worst-Case-Karte (kein ganzer Satz, prägnant).', example: '3 Monate Fee, kein einziger Abschluss' },
      { path: 'riskFraming.worstCaseSub', type: 'string', required: false, max: 120, style: 'Unterzeile Worst Case, ein kurzer Satz.', example: 'Reine Fixfee im Testzeitraum – ohne jeden gewonnenen Neukunden.' },
      { path: 'riskFraming.worstCaseStill', type: 'string', required: false, max: 60, style: 'Kurze Überleitung („Und selbst dann bekommst du:").', example: 'Und selbst in diesem Fall bekommst du:' },
      { path: 'riskFraming.worstCaseInsights', type: 'string[]', required: false, minItems: 3, maxItems: 5, count: '3–5', max: 60, style: 'Stichpunkte (KEIN Satz), was der Kunde TROTZDEM bekommt.', example: '["Validierte ICP-Daten", "Getestete Pain-Angles", …]' },
      { path: 'riskFraming.bestCaseSub', type: 'string', required: false, max: 120, style: 'Unterzeile Best Case, ein kurzer Satz.', example: 'Profit-Upside im starken Szenario.' },
      { path: 'riskFraming.bestCaseStill', type: 'string', required: false, max: 60, style: 'Kurze Überleitung Best Case.', example: 'Zusätzlich entsteht:' },
      { path: 'riskFraming.bestCaseExtras', type: 'string[]', required: false, minItems: 2, maxItems: 4, count: '2–4', max: 60, style: 'Stichpunkte (KEIN Satz), zusätzliche Best-Case-Vorteile.', example: '["Planbarer Akquise-Kanal", "Skalierbar", …]' },
      { path: 'riskFraming.note', type: 'longtext', required: false, max: 240, style: 'Ein Abschluss-Satz zur Risiko-Asymmetrie. KEINE Zahlen.', example: 'Der Kern des Cases ist die Asymmetrie …' },
    ],
  },
  {
    group: 'Fee vs. Upside & Executive Summary',
    fields: [
      { path: 'feeVsUpside.valueNote', type: 'longtext', required: false, max: 240, style: 'Ein Kernsatz zum Kundenwert über die gesamte Beziehung. KEINE Zahlen.', example: 'Der eigentliche Hebel liegt nicht nur im Erstabschluss …' },
      { path: 'executiveSummary.paragraphs', type: 'string[]', required: true, minItems: 1, maxItems: 3, count: '1–3', max: 300, style: 'Vollständige Sätze. Zusammenfassungs-Absätze. KEINE Zahlen.', example: '["… ist ein sehr guter SalesGod-Kunde …", "Der Case ist nicht perfekt …"]' },
      { path: 'executiveSummary.bestSegmentsTitle', type: 'string', required: false, max: 120, style: 'Ein Einleitungssatz vor den Segment-Chips.', example: 'Die größten Chancen liegen in:' },
      { path: 'executiveSummary.bestSegments', type: 'string[]', required: false, minItems: 3, maxItems: 8, count: '3–8', max: 60, style: 'Top-Segmente als kurze Chips (KEIN Satz).', example: '["Hausverwaltungen", "Hotels", "Lagerbetriebe"]' },
      { path: 'executiveSummary.closing', type: 'string', required: false, max: 200, style: 'Ein prägnanter Schluss-Satz.', example: 'Nicht über Masse. Sondern über präzise Zielgruppen …' },
    ],
  },
  {
    group: 'Research-Tab: Cold Outreach',
    fields: [
      { path: 'research.coldOutreach.score', type: 'number', required: true, numMin: 0, range: '0–max', style: 'Cold-Outreach-Score (≤ max).', example: '17' },
      { path: 'research.coldOutreach.max', type: 'number', required: true, numMin: 10, numMax: 20, range: '10–20', style: 'Maximalwert des Cold-Outreach-Scores.', example: '20' },
      { path: 'research.coldOutreach.headline', type: 'string', required: true, max: 45, style: 'MAXIMAL 6 Wörter, kurz und knackig (kein ganzer Satz, kein Punkt am Ende).', example: 'Hohes Cold-Outreach-Potential' },
    ],
  },
  {
    group: 'Research-Tab: Zielgruppen-Recherche (editierbare Chips)',
    fields: [
      { path: 'research.targetResearch.ideas', type: 'string[]', required: true, minItems: 4, maxItems: 10, count: '4–10', max: 50, style: 'Branchen/Zielgruppen als kurze Labels (KEIN Satz).', example: '["Hausverwaltungen & WEG-Verwalter", "Hotellerie (3–5 Sterne)"]' },
      { path: 'research.targetResearch.keywords', type: 'string[]', required: false, minItems: 0, maxItems: 12, count: '0–12', max: 40, style: 'Such-/Recherche-Keywords (Schlagworte). Standard leer.', example: '["Alarmanlage Gewerbe", "Zutrittskontrolle Unternehmen"]' },
      { path: 'research.targetResearch.hashtags', type: 'string[]', required: false, minItems: 0, maxItems: 12, count: '0–12', max: 30, style: 'Hashtags inkl. #, ein Wort. Standard leer.', example: '["#Sicherheitstechnik", "#Objektschutz"]' },
      { path: 'research.targetResearch.channels', type: 'string[]', required: false, minItems: 1, maxItems: 8, count: '1–8', max: 40, style: 'Kanäle (Schlagworte). „E-Mail (Cold Outreach)" sollte an Platz 1 stehen.', example: '["E-Mail (Cold Outreach)", "LinkedIn", "XING"]' },
      { path: 'research.targetResearch.decisionMakers', type: 'string[]', required: false, minItems: 3, maxItems: 8, count: '3–8', max: 40, style: 'Entscheider-Rollen (Schlagworte).', example: '["Geschäftsführer / Inhaber", "Facility Manager"]' },
      { path: 'research.targetResearch.regions', type: 'string[]', required: false, minItems: 0, maxItems: 12, count: '0–12', max: 40, style: 'Regionen (Schlagworte). Standard leer.', example: '["Rhein-Main", "Großraum München"]' },
      { path: 'research.targetResearch.revenueBands', type: 'string[]', required: false, minItems: 2, maxItems: 6, count: '2–6', max: 30, style: 'Umsatzgrößen als Bänder (Schlagworte).', example: '["2–10 Mio. €", "10–50 Mio. €"]' },
    ],
  },
  {
    group: 'Research-Tab: Outreach-Winkel (angles)',
    fields: [
      { path: 'research.angles[].title', type: 'string', required: false, max: 70, style: 'Aufhänger in Stichworten (KEIN Satz): das konkrete, stärkste Argument, mit dem dieses Unternehmen einen Prospect catcht. MINDESTENS 4 Angles, jeder klar verschieden.', example: 'Sicherheitslücken in Gewerbeobjekten' },
      { path: 'research.angles[].body', type: 'longtext', required: false, max: 320, style: '1 bis 2 vollständige Sätze: WARUM genau dieser Winkel bei Neukunden ODER Partnern zieht und welchen Nerv er trifft. Konkret, kein Allgemeinplatz, KEINE Zahlen.', example: 'Viele Unternehmen investieren erst nach einem Vorfall …' },
    ],
  },
  {
    group: 'Research-Tab: Kanal-Strategie (channelStrategy)',
    fields: [
      { path: 'research.channelStrategy.intro', type: 'longtext', required: false, max: 240, style: 'Ein Satz: wo Outreach insgesamt am stärksten ist.', example: 'Am stärksten ist die direkte Ansprache über E-Mail und Google, soziale Netzwerke spielen eine Nebenrolle.' },
      { path: 'research.channelStrategy.channels[].name', type: 'string', required: false, max: 40, style: 'Kanal/Netzwerk (z. B. Google Suche, LinkedIn, TikTok).', example: 'Google Suche' },
      { path: 'research.channelStrategy.channels[].fit', type: 'number', required: false, numMin: 0, numMax: 100, range: '0–100', style: 'Eignung 0–100 für DIESE Zielkunden.', example: '80' },
      { path: 'research.channelStrategy.channels[].recommendation', type: 'longtext', required: false, max: 220, style: 'Kurz: ob/warum/wie Outreach hier sinnvoll ist (oder nicht).', example: 'Entscheider suchen aktiv nach Lösungen, daher eignen sich Suchanzeigen und SEO besonders gut.' },
    ],
  },
  {
    group: 'Research-Tab: Angebot des Kunden (offerAnalysis)',
    fields: [
      { path: 'research.offerAnalysis.whatIsSold', type: 'longtext', required: false, max: 400, style: 'Was verkauft das Unternehmen konkret? Nur aus dem Info-Dump ableiten, nichts erfinden, sonst leer lassen.', example: 'Verkauf, Installation und Wartung von Alarmanlagen und Videoüberwachung für Gewerbeobjekte.' },
      { path: 'research.offerAnalysis.usp.text', type: 'longtext', required: false, max: 200, style: 'Ein kurzer einleitender Satz zur Differenzierung. Nur aus dem Info-Dump, sonst leer.', example: 'Die Differenzierung liegt in der Komplettbetreuung aus einer Hand.' },
      { path: 'research.offerAnalysis.usp.points', type: 'string[]', required: false, minItems: 0, maxItems: 4, count: '2–4', max: 160, style: '2 bis 4 USP-Stichpunkte (KEIN Satz, kein Punkt am Ende). Nur aus dem Info-Dump.', example: '["Eigene Technik-Teams statt Subunternehmer", "Reaktionszeit unter 24 Stunden"]' },
      { path: 'research.offerAnalysis.salesSetup.have', type: 'string[]', required: false, minItems: 0, maxItems: 5, count: '0–5', max: 160, style: 'Was im Vertrieb bereits vorhanden ist (Stichpunkte). Nur aus dem Info-Dump.', example: '["Vertriebsteam mit 2 bis 4 Personen", "Bestandskunden und Empfehlungen"]' },
      { path: 'research.offerAnalysis.salesSetup.missing', type: 'string[]', required: false, minItems: 0, maxItems: 5, count: '0–5', max: 160, style: 'Was fehlt / Lücken (Stichpunkte, Verneinungen erlaubt). Nur aus dem Info-Dump.', example: '["Kein aktives Outbound", "Keine systematische Nachverfolgung"]' },
      { path: 'research.offerAnalysis.noBrainerOffer', type: 'longtext', required: false, max: 400, style: 'No-Brainer-Offer nur wenn echt vorhanden, sonst leer (ehrlich, nichts erfinden).', example: 'Kostenloser Sicherheits-Check vor Ort inklusive Schwachstellen-Bericht.' },
    ],
  },
];

// ── Schreibregeln (für manuelle Eingabe UND KI-Generierung) ──
export const GENERATION_RULES = [
  'Sprache: Deutsch, professioneller B2B-Ton, sachlich und ehrlich – keine Werbe-Superlative, keine Heilsversprechen.',
  'Niemals konkrete Geldbeträge, Preise, ROI-Werte oder Szenario-Zahlen in Texte schreiben. Diese werden live aus config berechnet und würden sonst veralten.',
  'Schlagwort-Felder (Chips: ideas, keywords, hashtags, channels, decisionMakers, regions, revenueBands, bestSegments, *Insights, *Extras) sind KEINE Sätze: kurze Labels, kein Punkt am Ende.',
  'Satz-Felder (verdict.summary, *.assessment, *.note, angles[].body, executiveSummary.paragraphs) sind vollständige Sätze. leverage.negative/positive sind GENAU EIN Satz. coldOutreach.headline ist KEIN Satz, sondern maximal 6 Wörter, kurz und knackig.',
  'company.industry und company.audience sind kommagetrennte Aufzählungen, KEIN Satz und ohne Punkt am Ende.',
  'KEINE Gedankenstriche / KI-Bindestriche. Kein langer und kein kurzer Gedankenstrich. Stattdessen Komma, Punkt oder neuer Satz. Echte Bindestriche in Komposita (z. B. Lager- und Logistikbetriebe) sind erlaubt.',
  'Datenschutz: KEINE Klarnamen natürlicher Personen, keine privaten Kontaktdaten (E-Mail/Telefon/Adresse einzelner Personen) ausgeben. Entscheider nur als Rolle/Funktion (z. B. Geschäftsführung, Facility Management).',
  'Längen sind harte Obergrenzen (siehe Spalte „Länge/Range"). Bei Überschreitung blockiert die Validierung das Speichern, lieber kürzer und prägnant.',
  'Der Score wird NICHT geschrieben, sondern berechnet. Über researchScores & coldOutreach nur die Teil-Scores liefern (score ≤ max).',
  'Ehrlichkeit vor Optimismus: Schwächen (langer Sales Cycle, Erklärungsbedarf, breite Streuung) klar benennen statt schönreden.',
  'Sales-Angles (research.angles) sind das Herzstück der Ansprache: Liefere MINDESTENS 4 klar unterschiedliche, scharfe Angles, niemals generisches Geschwätz. Jeder Angle ist EIN konkreter Aufhänger, mit dem genau DIESES Unternehmen potenzielle Neukunden UND Partner/Multiplikatoren wirklich catcht, also das stärkste Argument, das einen Interessenten hinter dem Vorhang hervorlockt und zur Reaktion bringt. Leite die Angles aus den echten Stärken, USPs, Pains der Zielgruppe und Kauftriggern dieses Unternehmens ab. Variiere die Blickwinkel bewusst (z. B. konkreter Schmerz/Risiko, verpasste Chance oder Wettbewerbsvorteil, Effizienz/Zeitgewinn, Vertrauen/Referenzen, Partner- und Multiplikatoren-Hebel). Keine austauschbaren Worthülsen, jeder Angle muss für dieses Unternehmen treffen.',
];

// ── Validierung (data-driven aus FIELD_DOCS) ──
// Gibt { valid, errors[], warnings[] } zurück. errors blockieren das Speichern,
// warnings sind nur Hinweise. Alle in FIELD_DOCS dokumentierten Limits werden
// hier auch tatsächlich erzwungen (eine Quelle der Wahrheit).
const getByPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

const checkScalar = (val, f, label, E, W) => {
  if (f.type === 'bool') {
    if (val !== undefined && typeof val !== 'boolean') E(`${label} muss true/false sein.`);
    return;
  }
  if (f.type === 'number' || f.type === 'percent') {
    if (val === undefined || val === null) { if (f.required) E(`${label} fehlt (Zahl erforderlich).`); return; }
    if (typeof val !== 'number' || !isFinite(val)) { E(`${label} muss eine Zahl sein.`); return; }
    if (f.numMin !== undefined && val < f.numMin) E(`${label} ist kleiner als das Minimum (${val} < ${f.numMin}).`);
    if (f.numMax !== undefined && val > f.numMax) E(`${label} ist größer als das Maximum (${val} > ${f.numMax}).`);
    return;
  }
  if (f.type === 'enum') {
    if (val === undefined || val === null) { if (f.required) E(`${label} fehlt.`); return; }
    if (!f.enumVals.includes(val)) E(`${label} muss einer von [${f.enumVals.join(', ')}] sein.`);
    return;
  }
  // string / longtext
  const s = typeof val === 'string' ? val : '';
  if (val !== undefined && val !== null && typeof val !== 'string') { E(`${label} muss Text sein.`); return; }
  if (s.trim().length === 0) { if (f.required) E(`${label} fehlt (Pflichttext).`); else W(`${label} ist leer.`); return; }
  if (f.max && s.length > f.max) E(`${label} ist zu lang (${s.length}/${f.max} Zeichen).`);
};

const checkStringArray = (arr, f, label, E, W) => {
  if (arr === undefined || arr === null) { if (f.required) E(`${label} fehlt (Array erforderlich).`); return; }
  if (!Array.isArray(arr)) { E(`${label} muss ein Array sein.`); return; }
  if (arr.some((x) => typeof x !== 'string')) E(`${label} darf nur Strings enthalten.`);
  const items = arr.filter((x) => typeof x === 'string' && x.trim().length > 0);
  if (f.minItems !== undefined && items.length < f.minItems) {
    if (f.required) E(`${label} braucht mind. ${f.minItems} Einträge (aktuell ${items.length}).`);
    else if (items.length > 0) W(`${label} hat weniger als empfohlen (${items.length}/${f.minItems}).`);
  }
  if (f.maxItems !== undefined && items.length > f.maxItems) E(`${label} hat zu viele Einträge (${items.length}/${f.maxItems}).`);
  arr.forEach((x, i) => {
    if (typeof x === 'string' && f.max && x.length > f.max) E(`${label}[${i}] ist zu lang (${x.length}/${f.max} Zeichen).`);
  });
};

export const validateReport = (r) => {
  const errors = [];
  const warnings = [];
  const E = (m) => errors.push(m);
  const W = (m) => warnings.push(m);

  if (!isPlainObject(r)) {
    return { valid: false, errors: ['Report ist kein gültiges Objekt.'], warnings: [] };
  }

  FIELD_DOCS.forEach((section) => {
    section.fields.forEach((f) => {
      if (f.path.includes('[].')) {
        const [arrPath, sub] = f.path.split('[].');
        const arr = getByPath(r, arrPath);
        if (!Array.isArray(arr)) return; // Array-Existenz wird ggf. separat geprüft
        arr.forEach((el, i) => checkScalar(getByPath(el, sub), f, `${arrPath}[${i}].${sub}`, E, W));
      } else if (f.type === 'string[]') {
        checkStringArray(getByPath(r, f.path), f, f.path, E, W);
      } else {
        const val = getByPath(r, f.path);
        if (f.nullable && val === null) return;
        checkScalar(val, f, f.path, E, W);
      }
    });
  });

  // Cross-Field-Regeln
  if (!Array.isArray(r.researchScores) || r.researchScores.length === 0) {
    E('researchScores muss mind. 1 Dimension enthalten.');
  } else {
    r.researchScores.forEach((row, i) => {
      if (typeof row.score === 'number' && typeof row.max === 'number' && row.score > row.max) {
        E(`researchScores[${i}].score (${row.score}) darf nicht größer als max (${row.max}) sein.`);
      }
    });
  }
  const co = r.research && r.research.coldOutreach;
  if (co && typeof co.score === 'number' && typeof co.max === 'number' && co.score > co.max) {
    E(`research.coldOutreach.score (${co.score}) darf nicht größer als max (${co.max}) sein.`);
  }

  return { valid: errors.length === 0, errors, warnings };
};
